import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useApp, useT } from "@/context/AppContext";
import { usePartner } from "@/context/PartnerContext";
import { computeMonthly } from "@/lib/lease";
import { productName } from "@/lib/branding";
import Icon from "@/components/Icon";
import SubsidyHint from "@/components/SubsidyHint";
import { ArrowLeft, ArrowRight, Check, Plus, Save, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { tr } from "@/lib/i18n";

const PLACEMENTS = ["indoor", "outdoor", "both"];
const STEPS = 5;

export default function ConfiguratorPage() {
  const t = useT();
  const { lang, addToCart, user, formatPrice, leaseSettings } = useApp();
  const { partner, prefix } = usePartner();
  const loc = useLocation();
  const nav = useNavigate();
  const qs = new URLSearchParams(loc.search);

  // If we arrived from the homepage with ?sector=xxx (and optionally &usecase=yyy)
  // pre-fill those selections AND advance the wizard so the visitor doesn't repeat
  // a choice they already made. Steps:
  //   sector only        → step 2 (Which use-case?)
  //   sector + usecase   → step 3 (Choose hardware)
  const _initialSector = qs.get("sector") || "";
  const _initialUsecase = qs.get("usecase") || "";
  const _initialStep = _initialUsecase ? 3 : (_initialSector ? 2 : 1);

  const [step, setStep] = useState(_initialStep);
  const [sectors, setSectors] = useState([]);
  const [usecases, setUsecases] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [sectorSlug, setSectorSlug] = useState(_initialSector);
  const [usecaseSlug, setUsecaseSlug] = useState(_initialUsecase);
  const [placement, setPlacement] = useState("both");
  const [vendorSlug, setVendorSlug] = useState("");

  const [recs, setRecs] = useState({ hardware: [], software: [], bundles: [], addons: [] });
  const [selectedHw, setSelectedHw] = useState([]);   // ids
  const [selectedSw, setSelectedSw] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [pricingMode, setPricingMode] = useState("buy"); // buy | lease  (lease duration from engine settings)

  useEffect(() => {
    api.get("/sectors").then((r) => setSectors(r.data));
    api.get("/vendors").then((r) => setVendors(r.data));
  }, []);

  useEffect(() => {
    if (sectorSlug) {
      api.get("/usecases", { params: { sector: sectorSlug } }).then((r) => setUsecases(r.data));
    }
  }, [sectorSlug]);

  useEffect(() => {
    if (sectorSlug && usecaseSlug) {
      api.post("/configurator/recommend", {
        sector_slug: sectorSlug,
        usecase_slug: usecaseSlug,
        placement: placement === "both" ? null : placement,
        vendor_slug: vendorSlug || null,
      }).then((r) => setRecs(r.data));
    }
  }, [sectorSlug, usecaseSlug, placement, vendorSlug]);

  const hwMap = useMemo(() => Object.fromEntries(recs.hardware.map((h) => [h.id, h])), [recs.hardware]);
  const swMap = useMemo(() => Object.fromEntries(recs.software.map((s) => [s.id, s])), [recs.software]);
  const addonMap = useMemo(() => Object.fromEntries(recs.addons.map((a) => [a.id, a])), [recs.addons]);

  // Totals, hardware lease is computed by the deterministic lease engine on
  // total hardware investment; software keeps its own monthly price.
  const totals = useMemo(() => {
    let once = 0; let monthly = 0; let hwInvestment = 0;
    selectedHw.forEach((id) => {
      const h = hwMap[id]; if (!h) return;
      hwInvestment += h.purchase_price || 0;
    });
    if (pricingMode === "buy") {
      once += hwInvestment;
    } else {
      // lease, single monthly figure via engine (contract_duration from settings)
      const { monthly: leaseMonthly } = computeMonthly(hwInvestment, leaseSettings);
      monthly += leaseMonthly;
    }
    selectedSw.forEach((id) => {
      const s = swMap[id]; if (!s) return;
      if (s.one_time_setup) once += s.one_time_setup;
      if (s.price_monthly) monthly += s.price_monthly;
    });
    selectedAddons.forEach((id) => {
      const a = addonMap[id]; if (!a) return;
      once += a.price || 0;
    });
    return { once, monthly };
  }, [selectedHw, selectedSw, selectedAddons, pricingMode, hwMap, swMap, addonMap, leaseSettings]);

  const toggle = (arr, setArr, id) =>
    setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const canNext = () => {
    if (step === 1) return !!sectorSlug;
    if (step === 2) return !!usecaseSlug;
    if (step === 3) return selectedHw.length > 0;
    return true;
  };

  const addAllToCart = () => {
    // For lease mode: attribute the engine-computed lease proportionally to each kiosk
    // so per-item monthly is consistent with the total shown to the customer.
    const leaseEnabled = pricingMode !== "buy";
    const hwInvestment = selectedHw.reduce((sum, id) => sum + ((hwMap[id]?.purchase_price) || 0), 0);
    const totalMonthly = leaseEnabled ? computeMonthly(hwInvestment, leaseSettings).monthly : 0;

    selectedHw.forEach((id) => {
      const h = hwMap[id]; if (!h) return;
      let unit_price = 0; let monthly_price = 0; const mode = pricingMode;
      if (pricingMode === "buy") {
        unit_price = h.purchase_price;
      } else if (leaseEnabled && hwInvestment > 0) {
        // Allocate share of total monthly based on purchase-price weight.
        monthly_price = Math.round(((h.purchase_price || 0) / hwInvestment) * totalMonthly * 100) / 100;
      }
      addToCart({
        type: "hardware", ref_id: id, sku: h.sku, name: h.name,
        unit_price, monthly_price, pricing_mode: mode,
      });
    });
    selectedSw.forEach((id) => {
      const s = swMap[id]; if (!s) return;
      addToCart({
        type: "software", ref_id: id, sku: s.slug, name: s.name,
        unit_price: s.one_time_setup || 0, monthly_price: s.price_monthly || 0,
        pricing_mode: s.pricing_model === "monthly" ? "subscription" : "subscription",
      });
    });
    selectedAddons.forEach((id) => {
      const a = addonMap[id]; if (!a) return;
      const functionLabel = tr(a.function_i18n, lang) || a.name;
      const brandLabel = tr(a.function_i18n, lang) ? a.name : null;
      addToCart({
        type: "addon", ref_id: id, sku: a.sku, name: functionLabel, brand: brandLabel,
        unit_price: a.price || 0, monthly_price: 0, pricing_mode: "buy",
      });
    });
    toast.success(t.wizard.add_to_cart + " ✓");
    nav("/cart");
  };

  const saveConfig = async () => {
    if (!user) { nav("/login?redirect=/configurator"); return; }
    try {
      const hwInvBase = selectedHw.reduce((s, id) => s + ((hwMap[id]?.purchase_price) || 0), 0);
      const leaseTotalMonthly = pricingMode !== "buy"
        ? computeMonthly(hwInvBase, leaseSettings).monthly
        : 0;
      const items = [
        ...selectedHw.map((id) => {
          const h = hwMap[id];
          const share = (pricingMode !== "buy" && hwInvBase > 0)
            ? Math.round(((h.purchase_price || 0) / hwInvBase) * leaseTotalMonthly * 100) / 100
            : 0;
          return { type: "hardware", ref_id: id, sku: h.sku, name: h.name,
            unit_price: pricingMode === "buy" ? h.purchase_price : 0,
            monthly_price: share,
            pricing_mode: pricingMode };
        }),
        ...selectedSw.map((id) => {
          const s = swMap[id];
          return { type: "software", ref_id: id, sku: s.slug, name: s.name,
            unit_price: s.one_time_setup || 0, monthly_price: s.price_monthly || 0, pricing_mode: "subscription" };
        }),
        ...selectedAddons.map((id) => {
          const a = addonMap[id];
          return { type: "addon", ref_id: id, sku: a.sku, name: a.name, unit_price: a.price || 0, monthly_price: 0, pricing_mode: "buy" };
        }),
      ];
      await api.post("/me/configurations", {
        name: `Config ${new Date().toLocaleDateString()}`,
        sector_slug: sectorSlug, usecase_slug: usecaseSlug, items,
      });
      toast.success("Configuratie opgeslagen");
    } catch (e) { toast.error("Opslaan mislukt"); }
  };

  return (
    <div data-testid="configurator-page">
      {/* Progress */}
      <div className="sticky top-16 z-30 bg-[#0B110F]/95 backdrop-blur-md border-b border-[#243430]">
        <div className="container-x py-4">
          <div className="flex items-center justify-between mb-2 text-xs text-[#A8B3AD]">
            <span>{t.wizard.step} {step} {t.wizard.of} {STEPS}</span>
            <span className="font-semibold text-[#6FAA8A]">{Math.round((step / STEPS) * 100)}%</span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${(step / STEPS) * 100}%` }} />
          </div>
        </div>
      </div>

      <div className="container-x py-12">
        {/* BEX App Store ecosystem banner, alleen voor BEX/luxe partners */}
        {partner?.landing_use_luxe_theme && (partner?.ecosystem_partners?.length || 0) > 0 && (
          <div className="mb-10 border" style={{ background: "#FFFFFF", borderColor: "rgba(26,26,26,0.08)", borderRadius: 12, padding: "20px 24px" }} data-testid="configurator-ecosystem-banner">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
              <div className="flex-shrink-0">
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: "#09954F" }}>
                  Werkt naadloos met je BEX App Store
                </div>
                <div className="text-base mt-1" style={{ color: "#1A1A1A", fontFamily: "Mona Sans, Inter Tight, sans-serif", fontWeight: 700 }}>
                  Elke module hieronder koppelt direct met de partners die je al gebruikt.
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-5 ml-auto">
                {(partner.ecosystem_partners || []).slice(0, 7).map((ep) => (
                  <div key={ep.id} className="h-7 flex items-center" title={ep.name}>
                    {ep.logo_url ? (
                      <img src={ep.logo_url} alt={ep.name} className="max-h-7 max-w-[88px] object-contain opacity-80 hover:opacity-100 transition-opacity" />
                    ) : (
                      <span className="text-xs font-semibold" style={{ color: "#1A1A1A" }}>{ep.name}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <AnimatePresence mode="wait">
          {/* STEP 1 - Sector */}
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
              <h1 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">{t.wizard.s1}</h1>
              <p className="text-[#A8B3AD] mt-3 max-w-xl">{t.home.pick_sector_sub}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
                {(partner?.allowed_sector_slugs?.length
                  ? sectors.filter((s) => partner.allowed_sector_slugs.includes(s.slug))
                  : sectors
                ).map((s, i) => (
                  <motion.button key={s.slug} onClick={() => { setSectorSlug(s.slug); setStep(2); }}
                    initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    className={`card-interactive p-6 text-left ${sectorSlug === s.slug ? "ring-2 ring-[#6FAA8A]" : ""}`}
                    data-testid={`wizard-sector-${s.slug}-card`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="w-12 h-12 rounded-xl bg-[#6FAA8A]/12 grid place-items-center text-[#6FAA8A]">
                        <Icon name={s.icon} className="w-6 h-6" />
                      </div>
                      {sectorSlug === s.slug && <Check className="w-5 h-5 text-[#D97742]" />}
                    </div>
                    <div className="display text-lg font-semibold mt-4">{tr(s.name_i18n, lang)}</div>
                    <div className="text-sm text-[#A8B3AD] mt-1.5">{tr(s.description_i18n, lang)}</div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* STEP 2 - Usecase */}
          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
              <h1 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">{t.wizard.s2}</h1>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
                {usecases.map((u, i) => (
                  <motion.button key={u.slug} onClick={() => { setUsecaseSlug(u.slug); setStep(3); }}
                    initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    className={`card-interactive p-6 text-left ${usecaseSlug === u.slug ? "ring-2 ring-[#6FAA8A]" : ""}`}
                    data-testid={`wizard-usecase-${u.slug}-card`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="w-12 h-12 rounded-xl bg-[#D97742]/10 grid place-items-center text-[#D97742]">
                        <Icon name={u.icon} className="w-6 h-6" />
                      </div>
                      {usecaseSlug === u.slug && <Check className="w-5 h-5 text-[#D97742]" />}
                    </div>
                    <div className="display text-lg font-semibold mt-4">{tr(u.name_i18n, lang)}</div>
                    <div className="text-sm text-[#A8B3AD] mt-1.5">{tr(u.description_i18n, lang)}</div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* STEP 3 - Hardware */}
          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
              <h1 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">{t.wizard.s3}</h1>
              <div className="mt-6 flex flex-wrap gap-4 items-center">
                <div>
                  <div className="text-xs text-[#A8B3AD] mb-1">{lang === "en" ? "Placement" : lang === "de" ? "Platzierung" : lang === "fr" ? "Emplacement" : lang === "es" ? "Ubicación" : lang === "it" ? "Posizionamento" : lang === "da" ? "Placering" : "Plaatsing"}</div>
                  <div className="flex gap-2">
                    {PLACEMENTS.map((p) => (
                      <button key={p} onClick={() => setPlacement(p)}
                        className={`chip ${placement === p ? "!bg-[#1F3A2E] !text-white" : ""}`}
                        data-testid={`placement-${p}-chip`}>
                        {t.wizard[`placement_${p}`]}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[#A8B3AD] mb-1">{lang === "en" ? "Vendor" : lang === "de" ? "Hersteller" : lang === "fr" ? "Fournisseur" : lang === "es" ? "Proveedor" : lang === "it" ? "Fornitore" : lang === "da" ? "Leverandør" : "Leverancier"}</div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => setVendorSlug("")}
                      className={`chip ${!vendorSlug ? "!bg-[#1F3A2E] !text-white" : ""}`}
                      data-testid="vendor-all-chip">{t.wizard.vendor_all}</button>
                    {vendors.map((v) => (
                      <button key={v.slug} onClick={() => setVendorSlug(v.slug)}
                        className={`chip ${vendorSlug === v.slug ? "!bg-[#1F3A2E] !text-white" : ""}`}
                        data-testid={`vendor-${v.slug}-chip`}>{v.name}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Pricing mode toggle, single Lease option; contract duration comes from engine settings */}
              <div className="mt-6 inline-flex bg-[#141C19] border border-[#243430] rounded-lg p-1">
                {[
                  { key: "buy", label: t.wizard.pricing_buy },
                  { key: "lease", label: `Lease ${leaseSettings.default_contract_duration}m` },
                ].map((m) => (
                  <button key={m.key} onClick={() => setPricingMode(m.key)}
                    className={`px-4 py-1.5 text-xs rounded-md font-semibold transition ${pricingMode === m.key ? "bg-[#1F3A2E] text-white" : "text-[#A8B3AD]"}`}
                    data-testid={`pricing-${m.key}-button`}>
                    {m.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
                {recs.hardware.length === 0 && <div className="text-[#A8B3AD]">{lang === "en" ? "No hardware found for this combination." : lang === "de" ? "Keine Hardware für diese Kombination gefunden." : lang === "fr" ? "Aucun matériel trouvé pour cette combinaison." : lang === "es" ? "No se ha encontrado hardware para esta combinación." : lang === "it" ? "Nessun hardware trovato per questa combinazione." : lang === "da" ? "Ingen hardware fundet til denne kombination." : "Geen hardware gevonden voor deze combinatie."}</div>}
                {recs.hardware.map((h, i) => {
                  const selected = selectedHw.includes(h.id);
                  const detailHref = `${prefix}/product/${h.slug || h.id}`;
                  return (
                    <motion.div key={h.id}
                      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                      className={`card-interactive overflow-hidden flex flex-col ${selected ? "ring-2 ring-[#6FAA8A]" : ""}`}
                      onClick={() => toggle(selectedHw, setSelectedHw, h.id)}
                      data-testid={`wizard-hw-${h.id}-card`}
                    >
                      <div className="product-plate h-56 relative overflow-hidden">
                        {h.image_url && (
                          <img
                            src={h.image_url}
                            alt={h.name}
                            className="absolute inset-0 w-full h-full object-contain p-6"
                            loading="lazy"
                          />
                        )}
                        {selected && (
                          <div className="absolute top-3 right-3 bg-[#1F3A2E] text-white w-8 h-8 rounded-full grid place-items-center z-10">
                            <Check className="w-4 h-4" />
                          </div>
                        )}
                        <Link
                          to={detailHref}
                          onClick={(e) => e.stopPropagation()}
                          className="absolute bottom-3 right-3 z-10 text-[10px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full bg-[#0B110F]/70 text-[#F4F1EA] border border-[#6FAA8A]/50 hover:bg-[#1F3A2E] transition"
                          data-testid={`wizard-hw-${h.id}-details`}
                        >
                          Details
                        </Link>
                      </div>
                      <div className="p-5 flex-1 flex flex-col">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Link
                            to={`${prefix}/catalog?vendor=${h.vendor_slug}`}
                            onClick={(e) => e.stopPropagation()}
                            className="chip chip-outline hover:text-[#6FAA8A] hover:border-[#6FAA8A] transition"
                            data-testid={`wizard-hw-${h.id}-vendor`}
                          >
                            {h.vendor_slug}
                          </Link>
                          <span className="chip chip-outline capitalize">{h.placement}</span>
                        </div>
                        <Link
                          to={detailHref}
                          onClick={(e) => e.stopPropagation()}
                          className="font-semibold text-[#F4F1EA] hover:text-[#6FAA8A] transition leading-snug"
                        >
                          {productName(h, partner)}
                        </Link>
                        <div className="mt-auto pt-3">
                          {pricingMode === "buy" ? (
                            <div className="display text-xl font-semibold text-[#6FAA8A]">{formatPrice(h.purchase_price)}</div>
                          ) : (
                            <div className="display text-xl font-semibold text-[#6FAA8A]">
                              {formatPrice(computeMonthly(h.purchase_price || 0, leaseSettings).monthly)}<span className="text-xs text-[#A8B3AD] font-medium">/mnd</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* STEP 4 - Software */}
          {step === 4 && (
            <motion.div key="s4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
              <h1 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">{t.wizard.s4}</h1>
              <p className="text-[#A8B3AD] mt-3">{lang === "en" ? "Stack software to complete your workflow." : lang === "de" ? "Kombiniere Software, um deinen Workflow zu vervollständigen." : lang === "fr" ? "Empilez des logiciels pour compléter votre flux de travail." : lang === "es" ? "Combina software para completar tu flujo de trabajo." : lang === "it" ? "Combina i software per completare il tuo flusso di lavoro." : lang === "da" ? "Kombinér software for at fuldende dit workflow." : "Stapel software om jouw workflow compleet te maken."}</p>

              {/* Payment terminals, placement-aware. Outdoor kiosks show ONLY Adyen S1U2 + Mollie IM30. */}
              {recs.addons.some((a) => a.category === "payment") && (
                <div data-testid="wizard-payment-section">
                  <div className="flex items-end justify-between mt-12 mb-2 flex-wrap gap-2">
                    <h3 className="display text-xl font-semibold">
                      {lang === "en" ? "Choose a payment terminal" : lang === "de" ? "Zahlungsterminal wählen" : lang === "fr" ? "Choisir un terminal de paiement" : lang === "es" ? "Elige un terminal de pago" : lang === "it" ? "Scegli un terminale di pagamento" : lang === "da" ? "Vælg en betalingsterminal" : "Kies een betaalterminal"}
                    </h3>
                    <span className="text-xs text-[#A8B3AD]" data-testid="wizard-payment-placement-note">
                      {placement === "outdoor"
                        ? (lang === "en" ? "Outdoor: only weatherproof terminals" : lang === "de" ? "Außen: nur wetterfeste Terminals" : lang === "fr" ? "Extérieur : terminaux étanches uniquement" : lang === "es" ? "Exterior: solo terminales resistentes a la intemperie" : lang === "it" ? "Esterno: solo terminali resistenti alle intemperie" : lang === "da" ? "Udendørs: kun vejrbestandige terminaler" : "Buiten: alleen weerbestendige terminals")
                        : (lang === "en" ? "Indoor: multiple options" : lang === "de" ? "Innen: mehrere Optionen" : lang === "fr" ? "Intérieur : plusieurs options" : lang === "es" ? "Interior: varias opciones" : lang === "it" ? "Interno: più opzioni" : lang === "da" ? "Indendørs: flere muligheder" : "Binnen: meerdere opties")}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    {recs.addons.filter((a) => a.category === "payment").map((a) => {
                      const selected = selectedAddons.includes(a.id);
                      return (
                        <div key={a.id}
                          className={`card-interactive p-4 ${selected ? "ring-2 ring-[#6FAA8A]" : ""}`}
                          onClick={() => toggle(selectedAddons, setSelectedAddons, a.id)}
                          data-testid={`wizard-payment-${a.id}-card`}
                        >
                          <div className="product-plate h-32 rounded-lg relative overflow-hidden mb-3 grid place-items-center">
                            {a.image_url ? (
                              <img src={a.image_url} alt={a.name} className="absolute inset-0 w-full h-full object-contain p-3" loading="lazy" />
                            ) : (
                              <Icon name="CreditCard" className="w-10 h-10 text-[#6FAA8A]" />
                            )}
                            {selected && (
                              <div className="absolute top-2 right-2 bg-[#1F3A2E] text-white w-7 h-7 rounded-full grid place-items-center z-10">
                                <Check className="w-4 h-4" />
                              </div>
                            )}
                          </div>
                          <span className="text-[10px] chip chip-outline">{a.placement === "both" ? "in/outdoor" : a.placement === "outdoor" ? "outdoor" : "indoor"}</span>
                          <div className="font-semibold text-sm mt-2 text-[#F4F1EA] leading-tight" data-testid={`wizard-payment-${a.id}-name`}>{a.name}</div>
                          <div className="text-xs text-[#A8B3AD] mt-1.5">{tr(a.description_i18n, lang) || ""}</div>
                          <div className="text-sm text-[#6FAA8A] font-semibold mt-2">{formatPrice(a.price)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
                {recs.software.map((s, i) => {
                  const selected = selectedSw.includes(s.id);
                  const provPath = s.provider_type && s.provider_slug
                    ? (s.provider_type === "vendor"
                      ? `${prefix}/leverancier/${s.provider_slug}`
                      : `${prefix}/over/${s.provider_slug}`)
                    : null;
                  return (
                    <motion.div key={s.id}
                      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                      className={`card-interactive p-5 ${selected ? "ring-2 ring-[#6FAA8A]" : ""}`}
                      onClick={() => toggle(selectedSw, setSelectedSw, s.id)}
                      data-testid={`wizard-sw-${s.id}-card`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="w-10 h-10 rounded-lg bg-[#6FAA8A]/15 text-[#6FAA8A] grid place-items-center">
                          <Icon name={s.icon} className="w-5 h-5" />
                        </div>
                        {selected ? <Check className="w-5 h-5 text-[#D97742]" /> : <Plus className="w-5 h-5 text-[#A8B3AD]" />}
                      </div>
                      {provPath ? (
                        <Link
                          to={provPath}
                          onClick={(e) => e.stopPropagation()}
                          className="block font-semibold text-[#F4F1EA] mt-3 hover:text-[#6FAA8A] transition"
                          data-testid={`wizard-sw-${s.id}-provider`}
                        >
                          {s.name}
                        </Link>
                      ) : (
                        <h4 className="font-semibold text-[#F4F1EA] mt-3">{s.name}</h4>
                      )}
                      <div className="text-sm text-[#A8B3AD] mt-1.5 leading-relaxed">{tr(s.description_i18n, lang)}</div>
                      <div className="mt-3 flex items-baseline justify-between gap-3">
                        <div className="text-sm text-[#6FAA8A] font-semibold">
                          {s.price_monthly ? `${formatPrice(s.price_monthly)}/mnd` : ""}
                          {s.price_per_transaction ? ` · ${formatPrice(s.price_per_transaction)}/txn` : ""}
                          {s.one_time_setup ? ` · setup ${formatPrice(s.one_time_setup)}` : ""}
                        </div>
                        {provPath && (
                          <Link
                            to={provPath}
                            onClick={(e) => e.stopPropagation()}
                            className="text-[10px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full bg-[#141C19] border border-[#6FAA8A]/30 text-[#A8B3AD] hover:text-[#6FAA8A] hover:border-[#6FAA8A] transition whitespace-nowrap"
                            data-testid={`wizard-sw-${s.id}-provider-link`}
                          >
                            {lang === "en" ? "More info →" : lang === "de" ? "Mehr Infos →" : lang === "fr" ? "Plus d'infos →" : lang === "es" ? "Más info →" : lang === "it" ? "Più info →" : lang === "da" ? "Mere info →" : "Meer info →"}
                          </Link>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <h3 className="display text-xl font-semibold mt-14 mb-5">{lang === "en" ? "Recommended add-ons" : lang === "de" ? "Empfohlene Add-ons" : lang === "fr" ? "Options recommandées" : lang === "es" ? "Complementos recomendados" : lang === "it" ? "Add-on consigliati" : lang === "da" ? "Anbefalede tilbehør" : "Aanbevolen add-ons"}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {recs.addons.filter((a) => a.category !== "payment").map((a) => {
                  const selected = selectedAddons.includes(a.id);
                  const functionLabel = tr(a.function_i18n, lang) || a.name;
                  const brandLabel = tr(a.function_i18n, lang) ? a.name : null;
                  return (
                    <div key={a.id}
                      className={`card-interactive p-4 ${selected ? "ring-2 ring-[#6FAA8A]" : ""}`}
                      onClick={() => toggle(selectedAddons, setSelectedAddons, a.id)}
                      data-testid={`wizard-addon-${a.id}-card`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="text-xs chip chip-outline">{a.category}</div>
                        {selected ? <Check className="w-4 h-4 text-[#D97742]" /> : <Plus className="w-4 h-4 text-[#A8B3AD]" />}
                      </div>
                      <div className="font-semibold text-sm mt-2 text-[#F4F1EA] leading-tight" data-testid={`wizard-addon-${a.id}-function`}>{functionLabel}</div>
                      {brandLabel && (
                        <div className="text-[11px] text-[#7A8580] mt-0.5 truncate" title={brandLabel} data-testid={`wizard-addon-${a.id}-brand`}>{brandLabel}</div>
                      )}
                      <div className="text-xs text-[#A8B3AD] mt-1.5">{tr(a.description_i18n, lang) || ""}</div>
                      <div className="text-sm text-[#6FAA8A] font-semibold mt-2">{formatPrice(a.price)}</div>
                    </div>
                  );
                })}
              </div>

              {recs.bundles.length > 0 && (
                <>
                  <h3 className="display text-xl font-semibold mt-14 mb-5">{t.wizard.best_practice}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {recs.bundles.map((b) => (
                      <div key={b.id} className="card-surface p-5 bg-gradient-to-br from-[#1F3A2E] to-[#2d4a3e] text-white">
                        <div className="chip chip-accent mb-3">{t.wizard.recommended} · {b.discount_pct}%</div>
                        <h4 className="display text-lg font-semibold">{tr(b.name_i18n, lang)}</h4>
                        <p className="text-sm opacity-80 mt-2">{tr(b.description_i18n, lang)}</p>
                        <button className="btn-accent mt-4" onClick={() => nav(`/bundles/${b.id}`)} data-testid={`wizard-bundle-${b.id}-link`}>
                          {lang === "en" ? "View" : lang === "de" ? "Ansehen" : lang === "fr" ? "Voir" : lang === "es" ? "Ver" : lang === "it" ? "Vedi" : lang === "da" ? "Se" : "Bekijk"} <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </motion.div>
          )}

          {/* STEP 5 - Summary */}
          {step === 5 && (
            <motion.div key="s5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
              <h1 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">{t.wizard.s5}</h1>

              {/* Subsidy hint, only show when there's an investment to subsidise */}
              {totals.once > 0 && (
                <div className="mt-8" data-testid="wizard-subsidy-hint">
                  <SubsidyHint amount={totals.once} variant="banner" />
                </div>
              )}

              <div className="grid lg:grid-cols-3 gap-8 mt-10">
                <div className="lg:col-span-2 space-y-4">
                  {selectedHw.map((id) => {
                    const h = hwMap[id];
                    return (
                      <div key={id} className="card-surface p-5 flex items-center gap-4">
                        {h.image_url && <img src={h.image_url} className="w-24 h-24 object-contain bg-[#1F2A26] rounded-lg" alt="" />}
                        <div className="flex-1">
                          <div className="font-semibold">{h.name}</div>
                          <div className="text-xs text-[#A8B3AD]">{h.sku} · {pricingMode}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-[#6FAA8A]">
                            {pricingMode === "buy" ? formatPrice(h.purchase_price) : `${formatPrice(computeMonthly(h.purchase_price || 0, leaseSettings).monthly)}/mnd`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {selectedSw.map((id) => {
                    const s = swMap[id];
                    return (
                      <div key={id} className="card-surface p-5 flex items-center gap-4">
                        <div className="w-16 h-16 rounded-lg bg-[#6FAA8A]/15 grid place-items-center">
                          <Icon name={s.icon} className="w-6 h-6 text-[#6FAA8A]" />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold">{s.name}</div>
                          <div className="text-xs text-[#A8B3AD]">software · {s.pricing_model}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-[#6FAA8A]">
                            {s.price_monthly ? `${formatPrice(s.price_monthly)}/mnd` : formatPrice(s.one_time_setup || 0)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {selectedAddons.map((id) => {
                    const a = addonMap[id];
                    const functionLabel = tr(a.function_i18n, lang) || a.name;
                    const brandLabel = tr(a.function_i18n, lang) ? a.name : null;
                    return (
                      <div key={id} className="card-surface p-5 flex items-center gap-4" data-testid={`summary-addon-${id}-row`}>
                        <div className="w-16 h-16 rounded-lg bg-[#D97742]/10 grid place-items-center">
                          <Icon name="Plus" className="w-6 h-6 text-[#D97742]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate" data-testid={`summary-addon-${id}-function`}>{functionLabel}</div>
                          {brandLabel && (
                            <div className="text-[11px] text-[#7A8580] truncate" title={brandLabel} data-testid={`summary-addon-${id}-brand`}>{brandLabel}</div>
                          )}
                          <div className="text-xs text-[#A8B3AD] mt-0.5">add-on · {a.category}</div>
                        </div>
                        <div className="text-right font-semibold text-[#6FAA8A]">{formatPrice(a.price)}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="card-surface p-6 h-fit sticky top-28">
                  <div className="text-xs uppercase tracking-widest text-[#A8B3AD] font-bold">{lang === "en" ? "Summary" : lang === "de" ? "Zusammenfassung" : lang === "fr" ? "Récapitulatif" : lang === "es" ? "Resumen" : lang === "it" ? "Riepilogo" : lang === "da" ? "Oversigt" : "Samenvatting"}</div>
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between text-sm"><span>{t.cart.one_time}</span><span className="font-semibold">{formatPrice(totals.once)}</span></div>
                    <div className="flex justify-between text-sm"><span>{t.cart.monthly}</span><span className="font-semibold">{formatPrice(totals.monthly)}</span></div>
                  </div>
                  <div className="mt-6 space-y-2">
                    <button className="btn-accent w-full justify-center" onClick={addAllToCart} data-testid="wizard-add-to-cart-button">
                      <ShoppingCart className="w-4 h-4" /> {t.wizard.add_to_cart} → {t.nav.cart}
                    </button>
                    <button className="btn-outline w-full justify-center" onClick={saveConfig} data-testid="wizard-save-config-button">
                      <Save className="w-4 h-4" /> {t.wizard.save_config}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Subsidy hint, visible from step 3 onwards once there is hardware investment.
            Helps the user see grant potential BEFORE the summary step. */}
        {(step === 3 || step === 4) && totals.once > 0 && (
          <div className="mt-10" data-testid="wizard-inline-subsidy-hint">
            <SubsidyHint amount={totals.once} variant="banner" />
          </div>
        )}

        {/* Nav buttons */}
        <div className="mt-10 flex items-center justify-between gap-3">
          <button className="btn-ghost" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} data-testid="wizard-prev-button">
            <ArrowLeft className="w-4 h-4" /> {t.wizard.back}
          </button>
          <div className="flex items-center gap-3">
            {/* Skip button, only on step 4 (software + add-ons). Step 5 is the summary, no skip needed. */}
            {step === 4 && (
              <button
                className="btn-ghost"
                onClick={() => {
                  setSelectedSw([]);
                  setSelectedAddons([]);
                  setStep(step + 1);
                }}
                data-testid="wizard-skip-button"
              >
                {t.wizard.skip || (lang === "en" ? "Skip" : lang === "de" ? "Überspringen" : lang === "fr" ? "Ignorer" : "Overslaan")}
              </button>
            )}
            {step < STEPS && (
              <button className="btn-primary" disabled={!canNext()} onClick={() => setStep(step + 1)} data-testid="wizard-next-button">
                {t.wizard.next} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
